import {
  createContext,
  useContext,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { missionPhotos, type HeroPhotos, type HeroTile } from '../assets';
import { Button, ButtonLink } from '../ui/Button';
import { cx } from '../ui/cx';
import { Icon, type IconName } from '../ui/Icon';
import type { ScopedLabel } from '../ui/scoped';
import type { NotSetInfo } from '../ui/Stat';
import { StatusPill, type StatusTone } from '../ui/StatusPill';
import { useBusy } from './busy';
import s from './Hero.module.css';

export type HeroVariant = 'full' | 'slim';

/** The person's header preference, as the signed-in layout provides it. */
export interface HeroPreference {
  /** The default header (My settings › Home header). A page's `variant` prop overrides it. */
  variant: HeroVariant;
  /** Saves the choice when the person collapses or expands a header, so it sticks. */
  onChange?: (variant: HeroVariant) => void;
}

const HeroVariantContext = createContext<HeroPreference>({ variant: 'full' });
export const HeroVariantProvider = HeroVariantContext.Provider;

/** The header preference the signed-in layout provides (the feedback report records it). */
export function useHeroPreference(): HeroPreference {
  return useContext(HeroVariantContext);
}

/** A hero action: a link (`href`) or a button (`onPress`). The first one is the primary. */
export type HeroAction = {
  label: string;
  /**
   * gold for celebration (employee) moments, cyan for work actions. Primary only. `ghost` keeps
   * even the first action an outline, for a page with no main action (G1's Export record).
   */
  tone?: 'gold' | 'cyan' | 'ghost';
  icon?: IconName;
} & ({ href: string; onPress?: never } | { onPress: () => void; href?: never });

interface HeroStatBase {
  /** Always scoped: "Maritime Airborne · cash left 2026". A bare "Cash left" does not compile. */
  label: ScopedLabel;
  /** Preformatted ("$160,405", "7,956", "7 days left"). */
  value: string;
  /** Context under the value ("$175,500 awarded · of $335,905"). */
  meta?: string;
  tone?: 'default' | 'gold' | 'muted';
}

/**
 * A glass number. It can carry one way on in Full: a control under it (`action`, or `notSet`'s
 * link), or the whole number as a link to where its count comes from (`href`, APP-044 as built
 * 2026-10-20). Never both: a control can't sit inside a link. Slim shows only the number.
 */
export type HeroStat = HeroStatBase &
  (
    | {
        /** The number depends on a setting that isn't set yet (e.g. a budget pool). */
        notSet?: NotSetInfo;
        /**
         * One way to act on the number, under it in Full: a link ("Set them up" on SYS1's
         * need-setup tile) or a button (SYS1's "Run now").
         */
        action?: HeroAction & { isDisabled?: boolean };
        href?: never;
      }
    | {
        /**
         * The whole number is a link to the list or table it counts, scoped and filtered (X1's
         * "approvals waiting" to Approvals, SA1's "elections past deadline" to SA4).
         */
        href: string;
        notSet?: never;
        action?: never;
      }
  );

/** A way back up a level ("‹ Console"): above the eyebrow in Full, an icon before the title in Slim. */
export interface HeroBack {
  href: string;
  /** What shows in Full: "Console". */
  label: string;
  /** The link's name, which includes the label: "Back to the System overview". */
  name: string;
}

/** One step of the work the page belongs to (SA2, SA3: Check → Tax → Letters). */
export interface HeroStep {
  label: string;
  state: 'done' | 'current' | 'next';
}

/**
 * Where the page's work stands, as a glass step tracker in Full (the SA2/SA3 boards) and a chip in
 * Slim: a scoped caption ("Claimed by you · 9:41 AM"), the steps, and a line under them.
 */
export interface HeroSteps {
  label: string;
  steps: readonly HeroStep[];
  meta?: string;
}

export interface HeroProps {
  /** Defaults to the person's choice in My settings. The collapse button overrides it here. */
  variant?: HeroVariant;
  eyebrow?: string;
  /** Cyan for work pages (default), gold for celebration moments. */
  eyebrowTone?: 'cyan' | 'gold';
  title: string;
  /**
   * The browser tab's name when the heading isn't one (a greeting on Home): "Home". Defaults to the
   * title. Either way it ends "· Areté MyRewards" (QA-022, WCAG 2.4.2).
   */
  documentTitle?: string;
  /** One line under the title (Full only). */
  sub?: string;
  /** Up to two actions; Slim shows only the first, as a small button. */
  actions?: readonly [HeroAction] | readonly [HeroAction, HeroAction];
  /** Glass blocks in Full, compact chips in Slim (up to three). */
  stats?: readonly HeroStat[];
  /** A step tracker in place of the glass numbers (a work page's place in its steps). */
  steps?: HeroSteps;
  /** Three tiles of up to three photos each; Ken Burns about 24 s, cross-fade about 9 s. */
  photos?: HeroPhotos;
  /** Keeps the progress rule flowing (also on while anything uses `useBusy()`). */
  busy?: boolean;
  /** Called when the person collapses or expands the header. */
  onVariantChange?: (variant: HeroVariant) => void;
  /** A stable hook for end-to-end tests, on the page's `h1`. */
  titleTestId?: string;
  /** A link up a level, e.g. Admin tabs back to the console, or the record back to its list. */
  back?: HeroBack;
  /** The page's state beside the title ("Employee's choice · due Oct 21"), in words. */
  pill?: { label: string; tone: StatusTone };
  /** A second state word beside the first (a feedback report's kind next to its status). */
  extraPill?: { label: string; tone: StatusTone };
}

/** An award ID ("SDB-2026-0157", "SPOT-2026-0031"), the token a title must never split. */
const AWARD_ID = /\b([A-Z]{2,5}-\d{4}-\d{3,6})\b/;

/**
 * Text with each award ID kept on one line (APP-087 §1). Browsers may break after a hyphen, so a
 * phone showed "SDB-2026-" over "0157"; the ID wraps as a whole and the words around it as usual.
 */
export function keepIdsWhole(text: string): ReactNode {
  const parts = text.split(AWARD_ID);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className={s.id}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

const KEN_BURNS = [s.kbA, s.kbB, s.kbC] as const;
const FADE_DELAYS = [0, -18, -9] as const;

function Triptych({ photos }: { photos: HeroPhotos }): ReactElement {
  return (
    <div className={s.photos} aria-hidden="true">
      {photos.map((tile: HeroTile, k) => (
        <span key={k} className={s.tile}>
          <span className={cx(s.kb, KEN_BURNS[k])} style={{ animationDelay: `${-8 * k}s` }}>
            {tile.slice(0, 3).map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className={cx(s.photo, tile.length > 1 && s.xf)}
                style={
                  tile.length > 1
                    ? { animationDelay: `${(FADE_DELAYS[i] ?? 0) + 1.5 * k}s` }
                    : undefined
                }
                decoding="async"
              />
            ))}
          </span>
        </span>
      ))}
    </div>
  );
}

function ActionControl({
  action,
  primary,
  slim,
}: {
  action: HeroAction;
  primary: boolean;
  slim: boolean;
}): ReactElement {
  const variant = primary && action.tone !== 'ghost' ? (action.tone ?? 'cyan') : 'ghost-on-dark';
  const size = slim ? 'sm' : 'lg';
  const icon = action.icon ? <Icon name={action.icon} size={slim ? 16 : 18} /> : undefined;
  if (action.href !== undefined) {
    return (
      <ButtonLink href={action.href} variant={variant} size={size} icon={icon}>
        {action.label}
      </ButtonLink>
    );
  }
  return (
    <Button onPress={action.onPress} variant={variant} size={size} icon={icon}>
      {action.label}
    </Button>
  );
}

/** The steps as a glass block: each says Done or is numbered; the current one is marked. */
function GlassSteps({ steps }: { steps: HeroSteps }): ReactElement {
  return (
    <div className={cx(s.glass, s.glassSteps)}>
      <p className={cx('eyebrow', s.glassLabel)}>{steps.label}</p>
      <ol className={s.stepList} aria-label="Steps">
        {steps.steps.map((step, i) => (
          <li
            key={step.label}
            className={cx(
              s.step,
              step.state === 'done' && s.stepDone,
              step.state === 'current' && s.stepCurrent,
            )}
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            <span className={s.stepMark} aria-hidden="true">
              {step.state === 'done' ? <Icon name="check" size={14} /> : String(i + 1)}
            </span>
            <span className={s.stepLabel}>
              {step.label}
              {step.state === 'done' && ' · Done'}
            </span>
          </li>
        ))}
      </ol>
      {steps.meta !== undefined && <p className={s.glassMeta}>{steps.meta}</p>}
    </div>
  );
}

function GlassStat({ stat }: { stat: HeroStat }): ReactElement {
  if (stat.notSet) {
    return (
      <div className={cx(s.glass, s.glassNotSet)}>
        <p className={cx('eyebrow', s.glassLabel)}>{stat.label}</p>
        <p className={cx('disp', s.notSetValue)}>Not set</p>
        <p className={s.notSetMessage}>{stat.notSet.message}</p>
        <ButtonLink
          href={stat.notSet.href}
          variant="ghost-on-dark"
          size="sm"
          className={s.notSetAction}
        >
          {stat.notSet.actionLabel}
        </ButtonLink>
      </div>
    );
  }
  const tone = cx(stat.tone === 'gold' && s.toneGold, stat.tone === 'muted' && s.toneMuted);
  const body = (
    <>
      <p className={cx('eyebrow', s.glassLabel)}>{stat.label}</p>
      <p className={cx('disp', s.glassValue)}>{stat.value}</p>
      {stat.meta !== undefined && <p className={s.glassMeta}>{stat.meta}</p>}
    </>
  );
  if (stat.href !== undefined) {
    // The whole tile is the link, so its name is what it counts: the scope, the number and the
    // line under it. The arrow says it goes somewhere; it isn't read out.
    return (
      <AriaLink href={stat.href} className={cx(s.glass, s.glassLink, tone)}>
        {body}
        <span className={s.glassGo} aria-hidden="true">
          <Icon name="arrowRight" size={16} />
        </span>
      </AriaLink>
    );
  }
  return (
    <div className={cx(s.glass, tone)}>
      {body}
      {stat.action !== undefined && <StatAction action={stat.action} />}
    </div>
  );
}

/** A tile's own way on, an outline on the glass. */
function StatAction({ action }: { action: HeroAction & { isDisabled?: boolean } }): ReactElement {
  const icon = action.icon ? <Icon name={action.icon} size={16} /> : undefined;
  if (action.href !== undefined)
    return (
      <ButtonLink
        href={action.href}
        variant="ghost-on-dark"
        size="sm"
        icon={icon}
        className={s.notSetAction}
      >
        {action.label}
      </ButtonLink>
    );
  return (
    <Button
      onPress={action.onPress}
      variant="ghost-on-dark"
      size="sm"
      icon={icon}
      isDisabled={action.isDisabled === true}
      className={s.notSetAction}
    >
      {action.label}
    </Button>
  );
}

/** The gradient rule under the hero. It sweeps on mount and flows while busy. */
export function HeroRule({ busy }: { busy: boolean }): ReactElement {
  return (
    <>
      <div className={s.rule} data-busy={busy} aria-hidden="true" />
      <span className="visually-hidden" role="status">
        {busy ? 'Working on it…' : ''}
      </span>
    </>
  );
}

/**
 * The page hero in two forms. Full: dark band, photo triptych on the right, eyebrow, title, one-line
 * sub, up to two actions and floating glass numbers. Slim: one row with the title, up to three
 * scoped chips and the primary action. The chevron at the top right switches between them.
 */
export function Hero({
  variant: variantProp,
  eyebrow,
  eyebrowTone = 'cyan',
  title,
  documentTitle,
  sub,
  actions,
  stats = [],
  steps,
  photos = missionPhotos,
  busy = false,
  onVariantChange,
  titleTestId,
  back,
  pill,
  extraPill,
}: HeroProps): ReactElement {
  const preference = useContext(HeroVariantContext);
  const variant = variantProp ?? preference.variant;
  const [override, setOverride] = useState<HeroVariant | null>(null);
  const current = override ?? variant;
  const slim = current === 'slim';
  const titleId = useId();
  const { busy: globalBusy } = useBusy();
  const primary = actions?.[0];

  const toggle = (): void => {
    const next: HeroVariant = slim ? 'full' : 'slim';
    setOverride(next);
    onVariantChange?.(next);
    preference.onChange?.(next);
  };

  return (
    <section
      className={cx(s.hero, slim && s.slim)}
      aria-labelledby={titleId}
      data-variant={current}
    >
      <AriaButton
        className={s.toggle}
        onPress={toggle}
        aria-expanded={!slim}
        aria-label={slim ? 'Expand header' : 'Collapse header'}
      >
        <span className={s.toggleRing}>
          <Icon name={slim ? 'chevronDown' : 'chevronUp'} size={16} />
        </span>
      </AriaButton>
      <Triptych photos={photos} />
      <div className={s.wash} aria-hidden="true" />
      <title>{`${documentTitle ?? title} · Areté MyRewards`}</title>
      {slim ? (
        <div className={s.slimInner}>
          {/* The way back, the title and its state stay together at the start of the bar. */}
          <div className={s.slimTitleRow}>
            {back !== undefined && (
              <AriaLink href={back.href} className={s.backIcon} aria-label={back.name}>
                <Icon name="arrowLeft" size={18} />
              </AriaLink>
            )}
            <h1
              id={titleId}
              tabIndex={-1}
              className={cx('disp', s.slimTitle)}
              data-testid={titleTestId}
            >
              {keepIdsWhole(title)}
            </h1>
            {extraPill !== undefined && (
              <StatusPill tone={extraPill.tone} label={extraPill.label} className={s.pill} />
            )}
            {pill !== undefined && (
              <StatusPill tone={pill.tone} label={pill.label} className={s.pill} />
            )}
          </div>
          {(stats.length > 0 || primary || steps !== undefined) && (
            <div className={s.chips}>
              {steps !== undefined && (
                <span className={s.chip}>
                  <span className={cx('eyebrow', s.chipLabel)}>{steps.label}</span>
                  <span className={cx('disp', s.chipValue)}>
                    {steps.steps.find((st) => st.state === 'current')?.label ??
                      (steps.steps.every((st) => st.state === 'done') ? 'Done' : '')}
                  </span>
                </span>
              )}
              {stats.slice(0, 3).map((stat) => (
                <span
                  key={stat.label}
                  className={cx(
                    s.chip,
                    stat.tone === 'gold' && s.toneGold,
                    (stat.tone === 'muted' || stat.notSet) && s.toneMuted,
                  )}
                >
                  <span className={cx('eyebrow', s.chipLabel)}>{stat.label}</span>
                  <span className={cx('disp', s.chipValue)}>
                    {stat.notSet ? 'Not set' : stat.value}
                  </span>
                </span>
              ))}
              {primary && <ActionControl action={primary} primary slim />}
            </div>
          )}
        </div>
      ) : (
        <div className={s.fullInner}>
          <div className={s.copy}>
            {back !== undefined && (
              <AriaLink href={back.href} className={s.back} aria-label={back.name}>
                <Icon name="arrowLeft" size={16} />
                <span>{back.label}</span>
              </AriaLink>
            )}
            {eyebrow !== undefined && (
              <p className={cx('eyebrow', s.eyebrow, eyebrowTone === 'gold' && s.eyebrowGold)}>
                {keepIdsWhole(eyebrow)}
              </p>
            )}
            <div className={s.titleRow}>
              <h1
                id={titleId}
                tabIndex={-1}
                className={cx('disp', s.title)}
                data-testid={titleTestId}
              >
                {keepIdsWhole(title)}
              </h1>
              {extraPill !== undefined && (
                <StatusPill tone={extraPill.tone} label={extraPill.label} className={s.pill} />
              )}
              {pill !== undefined && (
                <StatusPill tone={pill.tone} label={pill.label} className={s.pill} />
              )}
            </div>
            {sub !== undefined && <p className={s.sub}>{sub}</p>}
            {actions && actions.length > 0 && (
              <div className={s.actions}>
                {actions.map((action, index) => (
                  <ActionControl
                    key={action.label}
                    action={action}
                    primary={index === 0}
                    slim={false}
                  />
                ))}
              </div>
            )}
          </div>
          {steps !== undefined ? (
            <div className={s.stats} data-count={1}>
              <GlassSteps steps={steps} />
            </div>
          ) : (
            stats.length > 0 && (
              <div className={s.stats} data-count={stats.length}>
                {stats.map((stat) => (
                  <GlassStat key={stat.label} stat={stat} />
                ))}
              </div>
            )
          )}
        </div>
      )}
      <HeroRule busy={busy || globalBusy} />
    </section>
  );
}
